import {
  Input,
  Table,
  Modal,
  Form,
  Upload,
  Button,
  Row,
  Col,
  Image,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../constants/Constants";

const Product = () => {
  const userId = localStorage.getItem("userId");
  const [product, setProduct] = useState([]);
  const [isAddImageModalVisible, setIsAddImageModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [updatedBoolean, setUpdatedBoolean] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [imageFiles, setImageFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const selectedProductRef = useRef(null);

  /* ---------------- SHOW IMAGE MODAL ---------------- */
  const showAddImageModal = (product) => {
    const initialFiles = {};
    for (let i = 1; i <= 6; i++) {
      if (product[`prodImg${i}`]) {
        initialFiles[i] = { url: product[`prodImg${i}`], file: null };
      } else {
        initialFiles[i] = { url: null, file: null };
      }
    }

    setImageFiles(initialFiles);
    setSelectedProduct(product);
    selectedProductRef.current = product;
    setIsAddImageModalVisible(true);
  };

  const hideAddImageModal = () => {
    setSelectedProduct(null);
    setIsAddImageModalVisible(false);
    setImageFiles({});
  };

  /* ---------------- FETCH PRODUCTS (NEW TABLE) ---------------- */
  useEffect(() => {
    fetchData();
  }, [updatedBoolean]);

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/products/allProductList`
      );

      const items = response?.data || [];
      const sortedItems = items.sort(
        (a, b) => new Date(b.createdTime) - new Date(a.createdTime)
      );

      setProduct(sortedItems);
      setFilteredProducts(sortedItems);
    } catch (error) {
      console.log(error);
    }
  };

  /* ---------------- SEARCH ---------------- */
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filtered = product.filter(
      (p) =>
        p.productName?.toLowerCase().includes(value) ||
        p.productId?.toLowerCase().includes(value)
    );

    setFilteredProducts(filtered);
  };

  /* ---------------- IMAGE UPLOAD ---------------- */
  const handleImageUpload = (file, sequence) => {
    setImageFiles((prev) => ({
      ...prev,
      [sequence]: { ...prev[sequence], file },
    }));
    return false;
  };

  const handleImageSubmit = async () => {
    try {
      setLoading(true);
      const formData = new FormData();

      Object.keys(imageFiles).forEach((sequence) => {
        const { file, url } = imageFiles[sequence];
        if (file instanceof File) {
          formData.append(`prodImg${sequence}`, file);
        }
        if (url) {
          formData.append(`prodImg${sequence}Url`, url);
        }
      });

      await axios.post(
        `${BASE_URL}/api/customProducts/update-images/${selectedProduct.productId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setUpdatedBoolean((prev) => !prev);
      message.success("Images uploaded successfully!");
      hideAddImageModal();
    } catch (error) {
      console.error(error);
      message.error("Failed to upload images!");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- TABLE COLUMNS (ALIGNED WITH NEW TABLE) ---------------- */
  const columns = [
    {
      title: "Product Id",
      dataIndex: "productId",
      key: "productId",
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Price",
      dataIndex: "productPrice",
      key: "productPrice",
      render: (price) => `₹${price || 0}`,
    },
    {
      title: "Stock",
      dataIndex: "minStk",   // ✅ NEW TABLE FIELD
      key: "minStk",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          style={{ color: "#1890FF", border: "1px solid #1890FF" }}
          onClick={() => showAddImageModal(record)}
        >
          Update Images
        </Button>
      ),
    },
  ];

  /* ---------------- UI ---------------- */
  return userId ? (
    <React.Fragment>
      <Input
        placeholder="Search product by Name or Id"
        value={searchText}
        onChange={handleSearch}
        style={{
          width: "100%",
          marginBottom: 16,
          border: "1px solid #1890ff",
        }}
      />

      <Table
        dataSource={filteredProducts}
        columns={columns}
        rowKey={(record) => record.productId}
        pagination={{ pageSize: 10 }}
      />

      {/* IMAGE MODAL */}
      <Modal
        title={`Update Images for ${selectedProduct?.productName}`}
        visible={isAddImageModalVisible}
        onCancel={hideAddImageModal}
        footer={[
          <Button
            key="submit"
            style={{ color: "#1890FF", border: "1px solid #1890FF" }}
            onClick={handleImageSubmit}
            loading={loading}
          >
            Save Changes
          </Button>,
        ]}
      >
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4, 5, 6].map((sequence) => (
            <Col span={8} key={sequence}>
              <div style={{ textAlign: "center" }}>
                <Image
                  width={100}
                  height={100}
                  src={
                    imageFiles[sequence]?.url ||
                    "https://via.placeholder.com/100"
                  }
                  fallback="https://via.placeholder.com/100"
                />
                <Upload
                  accept="image/*"
                  beforeUpload={(file) =>
                    handleImageUpload(file, sequence)
                  }
                  showUploadList={false}
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ maxWidth: "100px" }}
                  >
                    {imageFiles[sequence]?.file?.name || "Upload"}
                  </Button>
                </Upload>
              </div>
            </Col>
          ))}
        </Row>
      </Modal>
    </React.Fragment>
  ) : (
    <span
      style={{
        fontSize: "20px",
        background: "white",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      }}
    >
      Sign in to see Products
    </span>
  );
};

export default Product;
